import { useCallback, useReducer, useState } from "react"
import type { Consumer } from "@rails/actioncable"
import { useActionCable } from "@/hooks/useActionCable"
import { postForm, postJson } from "@/lib/api"
import { captureEvent } from "@/lib/analytics"
import type {
  ChatAccess,
  ChatMessage,
  MentionRef,
  StreamEvent,
  ToolCallEvent,
} from "@/types/chat"
import type { ApiError } from "@/lib/api"

export interface ChatState {
  messages: ChatMessage[]
  toolCalls: ToolCallEvent[]
  isStreaming: boolean
  error: string | null
}

export type ChatAction =
  | { type: "send"; message: ChatMessage }
  | { type: "event"; event: StreamEvent }
  | { type: "send_failed"; error: string }
  | { type: "set_feedback"; messageId: number; rating: "positive" | "negative"; comment?: string | null }

let tempIdCounter = -1
function nextTempId(): number {
  return tempIdCounter--
}

export const initialChatState: ChatState = {
  messages: [],
  toolCalls: [],
  isStreaming: false,
  error: null,
}

function upsertAssistantToken(
  messages: ChatMessage[],
  messageId: number,
  content: string
): ChatMessage[] {
  const existing = messages.find((m) => m.id === messageId)
  if (existing) {
    return messages.map((m) =>
      m.id === messageId ? { ...m, content: m.content + content, streaming: true } : m
    )
  }

  const placeholderIndex = messages.findIndex(
    (m) => m.role === "assistant" && m.id < 0 && m.streaming
  )
  if (placeholderIndex >= 0) {
    return messages.map((m, index) =>
      index === placeholderIndex
        ? { ...m, id: messageId, content, streaming: true }
        : m
    )
  }

  const assistant: ChatMessage = {
    id: messageId,
    role: "assistant",
    content,
    created_at: new Date().toISOString(),
    streaming: true,
  }
  return [...messages, assistant]
}

function assistantPlaceholder(): ChatMessage {
  return {
    id: nextTempId(),
    role: "assistant",
    content: "",
    created_at: new Date().toISOString(),
    streaming: true,
  }
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "send":
      return {
        ...state,
        messages: [...state.messages, action.message, assistantPlaceholder()],
        isStreaming: true,
        toolCalls: [],
        error: null,
      }
    case "send_failed":
      return {
        ...state,
        isStreaming: false,
        error: action.error,
        messages: state.messages.filter(
          (m) => !(m.role === "assistant" && m.id < 0 && m.streaming && !m.content)
        ),
      }
    case "set_feedback":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId
            ? { ...m, feedback: { rating: action.rating, comment: action.comment } }
            : m
        ),
      }
    case "event": {
      const event = action.event
      switch (event.type) {
        case "token":
          return {
            ...state,
            messages: upsertAssistantToken(state.messages, event.message_id, event.content),
          }
        case "tool_call": {
          const next: ToolCallEvent = {
            tool: event.tool,
            status: event.status,
            message_id: event.message_id,
            result_summary: event.result_summary,
          }
          const idx = state.toolCalls.findIndex((t) => t.tool === event.tool)
          const toolCalls =
            idx >= 0
              ? state.toolCalls.map((t, i) => (i === idx ? next : t))
              : [...state.toolCalls, next]
          return { ...state, toolCalls }
        }
        case "complete":
          return {
            ...state,
            isStreaming: false,
            toolCalls: [],
            messages: state.messages.map((m) =>
              m.id === event.message_id
                ? {
                    ...m,
                    streaming: false,
                    input_tokens: event.input_tokens ?? m.input_tokens,
                    output_tokens: event.output_tokens ?? m.output_tokens,
                  }
                : m.role === "assistant" && m.id < 0 && m.streaming
                  ? { ...m, streaming: false }
                  : m
            ),
          }
        case "error":
          return {
            ...state,
            isStreaming: false,
            error: event.error,
            messages: state.messages
              .filter((m) => !(m.role === "assistant" && m.id < 0 && !m.content))
              .map((m) =>
                event.message_id && m.id === event.message_id
                  ? { ...m, streaming: false }
                  : m.role === "assistant" && m.id < 0
                    ? { ...m, streaming: false }
                    : m
              ),
          }
        default:
          return state
      }
    }
    default:
      return state
  }
}

interface UseChatOptions {
  chatId: number
  initialMessages: ChatMessage[]
  initialAccess: ChatAccess
  consumer?: Consumer
}

interface SendOptions {
  references?: MentionRef[]
  files?: File[]
}

export interface UseChatResult extends ChatState {
  chatAccess: ChatAccess
  showUpgradeModal: boolean
  dismissUpgradeModal: () => void
  sendMessage: (content: string, options?: SendOptions) => Promise<void>
  setFeedback: (messageId: number, rating: "positive" | "negative", comment?: string | null) => void
}

export function useChat({
  chatId,
  initialMessages,
  initialAccess,
  consumer,
}: UseChatOptions): UseChatResult {
  const [state, dispatch] = useReducer(chatReducer, {
    ...initialChatState,
    messages: initialMessages,
  })
  const [chatAccess, setChatAccess] = useState<ChatAccess>(initialAccess)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  useActionCable({
    chatId,
    consumer,
    onEvent: useCallback((event: StreamEvent) => {
      dispatch({ type: "event", event })
    }, []),
  })

  const sendMessage = useCallback(
    async (content: string, options: SendOptions = {}) => {
      const trimmed = content.trim()
      if (!trimmed) return

      const optimistic: ChatMessage = {
        id: nextTempId(),
        role: "user",
        content: trimmed,
        created_at: new Date().toISOString(),
      }
      dispatch({ type: "send", message: optimistic })

      try {
        const url = `/api/chats/${chatId}/messages`
        const files = options.files ?? []
        if (files.length > 0) {
          const form = new FormData()
          form.append("content", trimmed)
          if (options.references?.length) {
            form.append("references", JSON.stringify(options.references))
          }
          files.forEach((file) => form.append("attachments[]", file))
          await postForm<{ message_id: number }>(url, form)
        } else {
          await postJson<{ message_id: number }>(url, {
            content: trimmed,
            references: options.references ?? [],
          })
        }
        captureEvent("chat_message_sent", {
          chat_id: chatId,
          has_files: files.length > 0,
          has_references: (options.references?.length ?? 0) > 0,
        })

        if (!chatAccess.subscribed && chatAccess.remaining !== null) {
          setChatAccess((prev) => {
            const remaining = Math.max(0, (prev.remaining ?? 0) - 1)
            return { ...prev, remaining, allowed: remaining > 0 }
          })
        }
      } catch (err) {
        const apiError = err as ApiError
        if (apiError.status === 402) {
          setChatAccess((prev) => ({
            ...prev,
            allowed: false,
            remaining: 0,
            upgrade_url: apiError.body?.upgrade_url ?? prev.upgrade_url,
          }))
          setShowUpgradeModal(true)
          dispatch({ type: "send_failed", error: apiError.message })
          return
        }
        if (apiError.status === 422 && apiError.body?.error === "message_too_long") {
          const max = apiError.body.max_length
          const message =
            max != null
              ? `Message is too long (maximum ${max} characters).`
              : apiError.message
          dispatch({ type: "send_failed", error: message })
          return
        }
        const message = err instanceof Error ? err.message : "Failed to send message"
        dispatch({ type: "send_failed", error: message })
      }
    },
    [chatId, chatAccess.subscribed, chatAccess.remaining]
  )

  const dismissUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false)
  }, [])

  const setFeedback = useCallback(
    (messageId: number, rating: "positive" | "negative", comment?: string | null) => {
      dispatch({ type: "set_feedback", messageId, rating, comment })
    },
    []
  )

  return {
    ...state,
    chatAccess,
    showUpgradeModal,
    dismissUpgradeModal,
    sendMessage,
    setFeedback,
  }
}
